import { useState, useEffect, useCallback } from 'react';
import appState from '../state/AppStateContainer';
import { ServiceUser } from '../services/service-user';
import appEnum from './appEnum';

interface SortingState {
  orderBy: number;
  orderDirection: 'asc' | 'desc';
  orderByField: string;
}

interface TableSortingProps {
  sortingKey: string;
  defaultOrderBy: number;
  defaultOrderDirection: 'asc' | 'desc';
  defaultOrderByField: string;
  onOrderChange: (orderBy: number, orderDirection: 'asc' | 'desc', orderByField?: string) => void;
}

export const useTableSorting = (
  sortingKey: string,
  preferenceType: string = appEnum.userPrefrences.projectOverview
) => {
  const [sorting, setSortingState] = useState<SortingState>({
    orderBy: -1,
    orderDirection: 'asc',
    orderByField: ''
  });

  useEffect(() => {
    const loadSorting = () => {
      const preferences = appState.getProjectOverviewPreferences();
      const tableSortings = preferences?.tableSorting || {};
      const savedSorting = tableSortings[sortingKey];

      if (savedSorting) {
        setSortingState(savedSorting);
      }
    };

    loadSorting();
  }, [sortingKey]);

  const setSorting = useCallback(async (orderBy: number, orderDirection: 'asc' | 'desc', orderByField: string = '') => {
    const newSorting = { orderBy, orderDirection, orderByField };

    setSortingState(newSorting);

    const currentPreferences = appState.getProjectOverviewPreferences() || {};
    const tableSortings = currentPreferences.tableSorting || {};

    const updatedTableSortings = {
      ...tableSortings,
      [sortingKey]: newSorting
    };

    const updatedPreferences = {
      ...currentPreferences,
      tableSorting: updatedTableSortings
    };

    // Update appState locally so changes persist across navigation
    appState.setProjectOverviewPreferences(updatedPreferences);

    await ServiceUser.updatePreferences(
      appState.getUserId(),
      updatedPreferences,
      preferenceType,
    );
  }, [sortingKey, preferenceType]);

  const getSortingProps = useCallback((): TableSortingProps => ({
    sortingKey,
    defaultOrderBy: sorting.orderBy,
    defaultOrderDirection: sorting.orderDirection,
    defaultOrderByField: sorting.orderByField,
    onOrderChange: setSorting
  }), [sortingKey, sorting, setSorting]);

  return {
    sorting,
    setSorting,
    getSortingProps
  };
};


